/**
 * @fileoverview Authentication helper functions for server-side session access
 * 
 * @purpose Provides compatibility layer for server-side session access, abstracting Better Auth
 *          implementation details. Can be re-exported by derivative apps or extended with custom helpers.
 * 
 * @usage
 * - Re-export: `export * from '@korvo/core/derivative-app/auth-helpers'`
 * - Extend: Add custom helpers alongside re-exported ones
 * 
 * @ownership @korvo/core derivative-app helpers
 * 
 * @dependencies
 * - better-auth/server (getSession)
 * - @korvo/core/derivative-app/better-auth-factory (Session type)
 * 
 * @restrictions
 * - Server-side only (uses better-auth/server)
 */

import type { PrismaClient } from 'korvo-app-prisma';

import { ERR_AUTH_401, ERR_FORBIDDEN_403, ERR_INTERNAL_500 } from '../lib/error-codes';
import { KorvoError } from '../lib/korvo-error';
import { isUserSessionInvalidated } from '../lib/session-validation';
import type { BetterAuthInstance, Session as BetterAuthSession } from './better-auth-factory';

/** Detect DB connection timeout from Better Auth / Prisma (same as proxy). */
function isConnectionTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('connection terminated') && (msg.includes('timeout') || msg.includes('unexpectedly'))) {
      return true;
    }
  }
  if (error && typeof error === 'object' && 'cause' in error) {
    return isConnectionTimeoutError((error as { cause: unknown }).cause);
  }
  return false;
}

/**
 * Session type from Better Auth
 */
export type Session = BetterAuthSession;

/** Optional headers source for {@link getAuthenticatedSession} in Route Handlers (prefer `NextRequest.headers`). */
export type GetAuthenticatedSessionOptions = {
  requestHeaders?: Headers
}

/**
 * Result type for requireAdmin() function
 */
export type AdminCheckResult = {
  userId: string;
  email: string;
  name?: string | null;
};

/**
 * Get authenticated session (server-side)
 * 
 * This is a wrapper around Better Auth's getSession that provides a consistent API.
 * Returns null if user is not authenticated.
 * 
 * @param auth - Better Auth instance (optional, will use default if not provided)
 * @returns Session object or null if not authenticated
 * 
 * @example
 * ```typescript
 * import { getAuthenticatedSession } from '@korvo/core/derivative-app/auth-helpers';
 * 
 * export default async function Page() {
 *   const session = await getAuthenticatedSession();
 *   if (!session) {
 *     redirect('/auth/signin');
 *   }
 *   return <div>Hello {session.user.name}</div>;
 * }
 * ```
 */
export async function getAuthenticatedSession(
  auth?: BetterAuthInstance,
  options?: GetAuthenticatedSessionOptions
): Promise<Session | null> {
  if (!auth) {
    throw new KorvoError(ERR_INTERNAL_500, 1, {
      message: 'Better Auth instance is required. Provide auth parameter or ensure auth is available in the calling context.',
      status: 500,
    });
  }
  
  try {
    let headersList: Headers
    if (options?.requestHeaders) {
      headersList = options.requestHeaders
    } else {
      // Dynamic import to get headers in Next.js context (RSC, server actions). Route Handlers
      // should pass `request.headers` via `options.requestHeaders` so cookies match the incoming request.
      const { headers } = await import('next/headers');
      headersList = await headers();
    }
    let session: Awaited<ReturnType<BetterAuthInstance['api']['getSession']>>;
    try {
      session = await auth.api.getSession({
        headers: headersList,
      });
    } catch (firstError) {
      // Retry once on connection timeout: pool may have had a stale (server-closed) connection.
      // Second attempt typically gets a fresh connection. If DB is actually down, retry will fail and we throw 503 below.
      if (!isConnectionTimeoutError(firstError)) {
        throw firstError;
      }
      session = await auth.api.getSession({
        headers: headersList,
      });
    }
    
    if (!session) {
      return null;
    }
    
    // Normalize role to uppercase for consistent admin checks
    // Better Auth stores role as lowercase, but our code expects uppercase
    const rawRole = (session.user as { role?: string | null } | undefined)?.role;
    const normalizedRole = typeof rawRole === 'string' ? rawRole.toUpperCase() : null;
    
    const result = {
      ...session,
      user: {
        ...session.user,
        role: normalizedRole,
      },
    } as Session;

    // Enforce server-side session invalidation (e.g. after password change or user delete)
    const userId = (result.user as { id?: string | null })?.id;
    if (userId && isUserSessionInvalidated(userId)) {
      return null;
    }
    
    return result;
  } catch (error) {
    if (isConnectionTimeoutError(error)) {
      throw new KorvoError(ERR_INTERNAL_500, 2, {
        message:
          'Database unavailable. Start the database (e.g. pnpm exec korvo-script ensure-db) and restart the dev server. See docs/TROUBLESHOOTING.md § P1001.',
        status: 503,
      });
    }
    // Better Auth may throw if session is invalid
    // Return null to indicate no authenticated session
    return null;
  }
}

/**
 * Get session user ID
 * 
 * Helper function for extracting user ID from session
 * 
 * @param session - Session object or null
 * @returns User ID or null
 * 
 * @example
 * ```typescript
 * import { getSessionUserId } from '@korvo/core/derivative-app/auth-helpers';
 * 
 * const session = await getAuthenticatedSession();
 * const userId = getSessionUserId(session);
 * ```
 */
export function getSessionUserId(session: Session | null): string | null {
  return session?.user?.id ?? null;
}

/**
 * Get session user role
 * 
 * Helper function for extracting user role from session
 * 
 * @param session - Session object or null
 * @returns User role or null
 * 
 * @example
 * ```typescript
 * import { getSessionUserRole } from '@korvo/core/derivative-app/auth-helpers';
 * 
 * const session = await getAuthenticatedSession();
 * const role = getSessionUserRole(session);
 * if (role === 'ADMIN') {
 *   // Admin-only logic
 * }
 * ```
 */
export function getSessionUserRole(session: Session | null): string | null {
  return (session?.user as { role?: string | null } | undefined)?.role ?? null;
}

/**
 * Check if user is authenticated
 * 
 * @param session - Session object or null
 * @returns True if user is authenticated
 * 
 * @example
 * ```typescript
 * import { isAuthenticated } from '@korvo/core/derivative-app/auth-helpers';
 * 
 * const session = await getAuthenticatedSession();
 * if (!isAuthenticated(session)) {
 *   redirect('/auth/signin');
 * }
 * ```
 */
export function isAuthenticated(session: Session | null): boolean {
  return session !== null && session.user !== undefined;
}

/**
 * Check if user has admin role
 * 
 * @param session - Session object or null
 * @returns True if user is admin
 * 
 * @example
 * ```typescript
 * import { isAdmin } from '@korvo/core/derivative-app/auth-helpers';
 * 
 * const session = await getAuthenticatedSession();
 * if (!isAdmin(session)) {
 *   redirect('/');
 * }
 * ```
 */
export function isAdmin(session: Session | null): boolean {
  const role = getSessionUserRole(session);
  return role === 'ADMIN' || role === 'admin';
}

/**
 * Check if session belongs to a registered user (has a role; excludes future GUEST if added)
 *
 * Use when you need to distinguish "logged-in with a real account" from anonymous/guest.
 * Today this is equivalent to isAuthenticated(session); add GUEST role later and this will exclude it.
 *
 * @param session - Session object or null
 * @returns True if user has a role (USER or ADMIN)
 */
export function isRegisteredUser(session: Session | null): boolean {
  const role = getSessionUserRole(session);
  return typeof role === 'string' && role.length > 0;
}

/**
 * Load admin from DB by email or userId. Throws if not found or not ADMIN.
 */
async function resolveAdminFromDb(
  prisma: PrismaClient,
  email: string | null | undefined,
  userId: string | null | undefined
): Promise<AdminCheckResult> {
  if (!email && !userId) {
    throw new KorvoError(ERR_AUTH_401, 1, {
      message: 'Unauthorized: No session or email',
      status: 401,
    });
  }
  const user = await prisma.user.findUnique({
    where: email ? { email } : { id: userId as string },
    select: { id: true, role: true, name: true, email: true },
  });
  if (!user) {
    throw new KorvoError(ERR_FORBIDDEN_403, 1, {
      message: 'Forbidden: User not found',
      status: 403,
    });
  }
  const normalizedRole = typeof user.role === 'string' ? user.role.toUpperCase() : user.role;
  if (normalizedRole !== 'ADMIN') {
    throw new KorvoError(ERR_FORBIDDEN_403, 2, {
      message: 'Forbidden: Admin access required',
      status: 403,
    });
  }
  return { userId: user.id, email: user.email, name: user.name };
}

/**
 * Require admin access (throws error if not admin)
 * Session role ADMIN returns from session (no DB). Otherwise resolves from DB.
 *
 * @param auth - Better Auth instance (required)
 * @param prisma - PrismaClient instance (required)
 * @param isAllowedAdminEmail - Optional: treat email as admin even when DB says not ADMIN
 */
export async function requireAdmin(
  auth: BetterAuthInstance,
  prisma: PrismaClient,
  isAllowedAdminEmail?: (email: string | null | undefined) => boolean
): Promise<AdminCheckResult> {
  const session = await getAuthenticatedSession(auth);
  if (!session) {
    throw new KorvoError(ERR_AUTH_401, 2, {
      message: 'Unauthorized: No session or email',
      status: 401,
    });
  }
  const email = (session.user as { email?: string | null })?.email ?? null;
  const userId = (session.user as { id?: string | null })?.id ?? null;
  if (!email && !userId) {
    throw new KorvoError(ERR_AUTH_401, 3, {
      message: 'Unauthorized: No session or email',
      status: 401,
    });
  }
  const sessionRole = getSessionUserRole(session);
  if (sessionRole === 'ADMIN') {
    if (!userId || !email) {
      throw new KorvoError(ERR_AUTH_401, 4, {
        message: 'Unauthorized: Missing session identifiers',
        status: 401,
      });
    }
    return {
      userId,
      email,
      name: (session.user as { name?: string | null })?.name ?? null,
    };
  }
  try {
    return await resolveAdminFromDb(prisma, email, userId);
  } catch (err) {
    if (isAllowedAdminEmail?.(email)) {
      return {
        userId: userId!,
        email: email!,
        name: (session.user as { name?: string | null })?.name ?? null,
      };
    }
    throw err;
  }
}

/**
 * Create auth helpers bound to this app's auth and prisma.
 * getAuthenticatedSession: runs ensureBeforeSession() then getSession so Prisma is ready before first use.
 *
 * @param ensureBeforeSession - Call before getSession (e.g. ensureClient()) so the Prisma client is initialized before any session/DB access.
 */
export function createAuthHelpers(
  auth: BetterAuthInstance,
  prisma: PrismaClient,
  options: { ensureBeforeSession: () => Promise<unknown> }
): {
  getAuthenticatedSession: (sessionOptions?: GetAuthenticatedSessionOptions) => Promise<Session | null>;
  requireAdmin: () => Promise<AdminCheckResult>;
} {
  const { ensureBeforeSession } = options;
  return {
    getAuthenticatedSession: async (sessionOptions?: GetAuthenticatedSessionOptions) => {
      await ensureBeforeSession();
      return getAuthenticatedSession(auth, sessionOptions);
    },
    requireAdmin: () => requireAdmin(auth, prisma),
  };
}
